/**
 * Botón flotante de ayuda (bottom-right) que abre un drawer con:
 *  - Buscador de preguntas frecuentes
 *  - FAQ agrupadas por categoría (Accordion)
 *  - Link discreto al Centro de Ayuda completo (/ayuda)
 *
 * Sin videos, sin contactar soporte, sin llamada. Solo self-service.
 */
import { useState, useMemo } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import { HelpCircle, Search, Lightbulb, BookOpen, ArrowRight } from 'lucide-react';
import { faqCategories } from '@/lib/dashboardHelp';

export default function FloatingHelpButton() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query.trim()) return faqCategories;
    const q = query.toLowerCase();
    return faqCategories
      .map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (it) =>
            it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q),
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [query]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#F05D5E] text-white shadow-lg shadow-[#F05D5E]/30 transition-all hover:scale-105 hover:bg-[#d64431] focus:outline-none focus:ring-2 focus:ring-[#F05D5E]/50"
          data-testid="floating-help-btn"
          aria-label="Abrir ayuda rápida"
        >
          <HelpCircle className="h-6 w-6" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full max-w-md overflow-y-auto p-0 sm:max-w-lg">
        <SheetHeader className="border-b border-neutral-100 bg-gradient-to-br from-[#fff8f6] to-white px-6 py-5">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <Lightbulb className="h-5 w-5 text-[#F05D5E]" />
            Centro de Ayuda rápida
          </SheetTitle>
          <SheetDescription className="text-neutral-500">
            Encuentra respuestas rápidas a las preguntas más comunes.
          </SheetDescription>
        </SheetHeader>

        <div className="border-b border-neutral-100 px-6 py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar... ej. horarios, pagos, cancelar"
              className="pl-9"
              data-testid="help-search-input"
            />
          </div>
        </div>

        <div className="px-6 py-4">
          {filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-neutral-200 p-6 text-center">
              <p className="text-sm text-neutral-500">
                No encontramos preguntas para <span className="font-medium">&quot;{query}&quot;</span>.
              </p>
              <a
                href="/ayuda"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#F05D5E] hover:underline"
                data-testid="help-goto-full-center"
              >
                Ir al Centro de Ayuda completo
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
          ) : (
            filtered.map((cat) => (
              <div key={cat.category} className="mb-5">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                  {cat.category}
                </p>
                <Accordion type="single" collapsible className="space-y-1">
                  {cat.items.map((it, idx) => (
                    <AccordionItem
                      key={idx}
                      value={`${cat.category}-${idx}`}
                      className="rounded-lg border border-neutral-100 bg-white px-3"
                    >
                      <AccordionTrigger className="py-3 text-left text-sm font-medium hover:no-underline">
                        {it.q}
                      </AccordionTrigger>
                      <AccordionContent className="pb-3 pt-0 text-sm text-neutral-600">
                        {it.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-neutral-100 bg-neutral-50 px-6 py-4">
          <button
            type="button"
            onClick={() => {
              try { window.localStorage.removeItem('bookvia_tour_completed'); } catch { /* ignore */ }
              window.location.reload();
            }}
            className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-neutral-600 hover:text-[#F05D5E]"
            data-testid="help-relaunch-tour"
          >
            <Lightbulb className="h-3.5 w-3.5" />
            Ver tour de bienvenida otra vez
          </button>
          <p className="mb-2 text-xs text-neutral-500">¿No encontraste lo que buscabas?</p>
          <a
            href="/ayuda"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#F05D5E] hover:underline"
            data-testid="help-footer-link"
          >
            <BookOpen className="h-4 w-4" />
            Ver Centro de Ayuda completo
          </a>
        </div>
      </SheetContent>
    </Sheet>
  );
}
